import { type JSX, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useCurrentApp, useTheme, type AppThemeId, type ThemeMode } from '../hooks';

interface SettingsMenuProps {
  /** Whether the current user is a platform admin. Adds "Platform Admin" row. */
  isAdmin?: boolean;
  /** Called when the user clicks "Sign out". */
  onLogout?: () => void | Promise<void>;
  /** Desktop routes nest under /apps/:slug; PWA uses top-level /:slug. */
  variant?: 'desktop' | 'mobile';
}

/**
 * Routes that each app exposes for its own settings page. Keys are
 * `theme_id`s (see `useCurrentApp`). Value is the path segment appended
 * after the app's base route.
 *
 * NOTE: Smart News settings live under its own `news/*` sub-tree
 * (`/apps/smart-news/news/settings`), so the segment includes the prefix.
 *
 * Apps NOT in this map still get a "Settings" entry that routes to the
 * platform-level `/settings` page.
 */
const APP_SETTINGS_SEGMENT: Partial<Record<AppThemeId, string>> = {
  'smart-news': 'news/settings',
  surftime: 'settings',
  localtea: 'settings',
  'llm-chat': 'memory', // "NerdyBot settings" opens the user's memory page — everyone, not admin-gated
  bitzbuggy: 'notifications', // BitzBuggy exposes notification mute prefs as its settings entry
};

/**
 * Apps that expose a separate admin surface, distinct from their user-level
 * "{App} settings" entry above (PR #85 review, F2). Rendered as
 * "{App} admin" directly after the settings entry, admin-only.
 */
const APP_ADMIN_SEGMENT: Partial<Record<AppThemeId, string>> = {
  'llm-chat': 'admin',
};

const APP_DISPLAY_NAME: Record<AppThemeId, string> = {
  'nerdy-paradise': 'Platform',
  'llm-chat': 'NerdyBot',
  'smart-news': 'Smart News',
  surftime: 'SurfTime',
  localtea: 'LocalTea',
  'trading-agent': 'Trading Agent',
  bitzbuggy: 'BitzBuggy',
};

function buildAppSettingsPath(
  variant: 'desktop' | 'mobile',
  appId: AppThemeId,
): string | null {
  if (appId === 'nerdy-paradise') return null;
  const segment = APP_SETTINGS_SEGMENT[appId];
  if (!segment) return null;
  return variant === 'desktop'
    ? `/apps/${appId}/${segment}`
    : `/${appId}/${segment}`;
}

function buildAppAdminPath(
  variant: 'desktop' | 'mobile',
  appId: AppThemeId,
): string | null {
  if (appId === 'nerdy-paradise') return null;
  const segment = APP_ADMIN_SEGMENT[appId];
  if (!segment) return null;
  return variant === 'desktop'
    ? `/apps/${appId}/${segment}`
    : `/${appId}/${segment}`;
}

export function SettingsMenu({
  isAdmin = false,
  onLogout,
  variant = 'desktop',
}: SettingsMenuProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [themeOpen, setThemeOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const appId = useCurrentApp();
  const { theme, setTheme } = useTheme();

  // Click-outside close
  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (!rootRef.current?.contains(e.target as Node)) {
        setOpen(false);
        setThemeOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setOpen(false);
        setThemeOpen(false);
      }
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const appSettingsPath = buildAppSettingsPath(variant, appId);
  const appAdminPath = buildAppAdminPath(variant, appId);
  const platformSettingsPath = '/settings';
  const platformAdminPath = '/admin';

  function go(path: string) {
    setOpen(false);
    setThemeOpen(false);
    navigate(path);
  }

  function pickTheme(mode: ThemeMode) {
    setTheme(mode);
    setThemeOpen(false);
  }

  return (
    <div ref={rootRef} className="shell__settings-root" style={{ position: 'relative' }}>
      <button
        type="button"
        className="shell__icon-btn"
        aria-label="Settings menu"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v: boolean) => !v)}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.9.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.9 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.9l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.9.3h0a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5h0a1.7 1.7 0 0 0 1.9-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.9v0a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
        </svg>
      </button>

      {open && (
        // The dropdown lists ONLY routes that actually resolve today:
        //   - Theme (inline submenu)
        //   - The current app's settings (when it has one) — a user-level
        //     entry, visible to everyone (e.g. "NerdyBot settings" opens
        //     the memory page for any signed-in user)
        //   - The current app's admin surface (when it has one), admin-only
        //     — e.g. "NerdyBot admin" — kept separate from the settings
        //     entry above so the two are never confused (PR #85 review)
        //   - Platform settings (always)
        //   - Platform Admin (admin-only)
        //   - Sign out
        // The previous menu had Profile / Notifications / Connected accounts
        // / Privacy rows — those routes don't exist yet, so they've been
        // removed until the backing pages ship. Adding them back without
        // a target is worse than not listing them.
        <div role="menu" className="shell__menu" data-platform-menu>
          <button
            type="button"
            role="menuitem"
            className="shell__menu-item shell__menu-item--sub"
            onClick={() => setThemeOpen((v: boolean) => !v)}
            aria-expanded={themeOpen}
          >
            <span>Theme</span>
            <span className="shell__menu-meta">{theme}</span>
          </button>
          {themeOpen && (
            <div role="menu" className="shell__menu shell__submenu">
              <MenuItem label="Light" active={theme === 'light'} onClick={() => pickTheme('light')} />
              <MenuItem label="Dark" active={theme === 'dark'} onClick={() => pickTheme('dark')} />
              <MenuItem label="System" active={theme === 'system'} onClick={() => pickTheme('system')} />
            </div>
          )}

          {appSettingsPath && (
            <MenuItem
              label={`${APP_DISPLAY_NAME[appId]} settings`}
              onClick={() => go(appSettingsPath)}
            />
          )}
          {isAdmin && appAdminPath && (
            <MenuItem
              label={`${APP_DISPLAY_NAME[appId]} admin`}
              onClick={() => go(appAdminPath)}
            />
          )}
          <MenuItem label="Platform settings" onClick={() => go(platformSettingsPath)} />
          {isAdmin && (
            <MenuItem label="Platform Admin" onClick={() => go(platformAdminPath)} />
          )}

          <div className="shell__menu-sep" role="separator" />

          <MenuItem
            label="Sign out"
            destructive
            onClick={async () => {
              setOpen(false);
              if (onLogout) await onLogout();
            }}
          />
        </div>
      )}
    </div>
  );
}

function MenuItem({
  label,
  onClick,
  active,
  destructive,
}: {
  label: string;
  onClick: () => void;
  active?: boolean;
  destructive?: boolean;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      className={`shell__menu-item${active ? ' shell__menu-item--active' : ''}${destructive ? ' shell__menu-item--danger' : ''}`}
      onClick={onClick}
    >
      {label}
    </button>
  );
}
